import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnalyticsEvent, track } from "../lib/analytics";
import { useAuthStore } from "../store/auth";
import { toast } from "../store/toast";

/**
 * The three features that need a server: they exist for signed-out users too,
 * they just cannot run. The affordance stays visible and enabled (a missing
 * Share button reads as a missing feature) and activating it explains why and
 * opens sign-in instead of firing a request that would 401.
 */
export type SignInGateFeature = "share" | "history" | "import";

/**
 * Returns a guard: call it at the top of the handler and bail when it returns
 * true. Signed in (or parked, which still has no account here), it is a no-op
 * that returns false and the caller proceeds as always.
 */
export function useSignInGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localMode = useAuthStore((s) => s.user === null);

  return useCallback(
    (feature: SignInGateFeature): boolean => {
      if (!localMode) return false;
      track(AnalyticsEvent.LocalModeUpgradePrompted, { source: feature });
      toast(t(`gate.${feature}`));
      navigate("/login");
      return true;
    },
    [localMode, navigate, t],
  );
}
